/// <amd-module name="Test/TestPlansHubRefresh/Pivots/ExecutePivot/Component/TestPointsTableItemProvider" />
import { IOutcome, ITestPointItem } from "Test/TestPlansHubRefresh/Pivots/ExecutePivot/Redux/PointsContracts";
import { Outcome, TestPoint } from "TestPlanCommon/Contracts/TestPlan";
import { ObservableArray } from "VSS/Core/Observable";
import { IListSelection, ISimpleListCell } from "VSSUI/List";
import { ISelectionRange } from "VSSUI/Utilities/Selection";

export interface IPointsTableItem {
    TitleColumn: string;
    OutcomeColumn: ISimpleListCell;
    OrderColumn: string;
}

export class TestPointsTableItemProvider {
    private _items: ObservableArray<IPointsTableItem>;

    constructor() {
        this._items = new ObservableArray<IPointsTableItem>();
    }
    public getTestPointsForTable(
        testPointItems: ITestPointItem[],
        selection: IListSelection,
        filteredTestPoints?: number[]
    ): ObservableArray<IPointsTableItem> {
        // Clear the observable array if there are no test points
        if (!testPointItems || testPointItems.length == 0) {
            this._items.removeAll();
            return this._items;
        }

        // store the indices of the items that the user may have selected in the table (used during paging where we render all items)
        const selectedIndices: number[] = [];
        if (selection && selection.value && selection.value.length > 0) {
            const length: number = selection.value.length;
            for (let i = 0; i < length; i++) {
                const selectionRange: ISelectionRange = selection.value[i];

                if (selectionRange) {
                    for (let j = selectionRange.beginIndex; j <= selectionRange.endIndex; j++) {
                        selectedIndices.push(j);
                    }
                }
            }
        }

        // clear all the items first
        this._items.removeAll();

        // if filteredTestPoints is valid and there are no items in filtered test points, return empty items so an empty table can render
        if (filteredTestPoints && filteredTestPoints.length === 0) {
            return this._items;
        }

        // testPoints[] contains the stuff we want to render
        for (let i = 0; i < testPointItems.length; i++) {
            const testPointTableItem: IPointsTableItem = {
                TitleColumn: testPointItems[i].testCaseRef.title,
                OutcomeColumn: this._getOutcomeDetails(testPointItems[i].outcomeItem),
                OrderColumn: testPointItems[i].order,
                ...testPointItems[i].columnOptionValues
            };

            this._items.push(testPointTableItem);
        }

        // restore the selected indices so we maintain the user selected items in the new render
        if (selection && selectedIndices && selectedIndices.length > 0) {
            for (const idx of selectedIndices) {
                selection.select(idx, 1, true);
            }
        }

        return this._items;
    }

    public _getTestTitle(testPoint: TestPoint): string {
        let testTitle = "";
        if (testPoint && testPoint.testCaseReference && testPoint.testCaseReference.name) {
            testTitle = testPoint.testCaseReference.name;
        }

        return testTitle;
    }

    public _getTesterName(testPoint: TestPoint): string {
        let testerName = "";
        if (testPoint && testPoint.tester && testPoint.tester.displayName) {
            testerName = testPoint.tester.displayName;
        }

        return testerName;
    }

    public _getConfigurationName(testPoint: TestPoint): string {
        let configName = "";
        if (testPoint && testPoint.configuration && testPoint.configuration.name) {
            configName = testPoint.configuration.name;
        }

        return configName;
    }

    public _getOutcomeDetails(pointOutcome: IOutcome): ISimpleListCell {
        let details: ISimpleListCell = {} as ISimpleListCell;

        if (pointOutcome.outcome === Outcome.Passed) {
            details = { iconProps: { iconName: "CompletedSolid", className: "pass-icon" } };
        } else if (pointOutcome.outcome === Outcome.Failed) {
            details = { iconProps: { iconName: "StatusErrorFull", className: "fail-icon" } };
        } else if (pointOutcome.outcome === Outcome.Blocked) {
            details = { iconProps: { iconName: "Blocked2Solid", className: "block-icon" } };
        } else if (pointOutcome.outcome === Outcome.NotApplicable) {
            details = { iconProps: { iconName: "BlockedSolid", className: "not-applicable-icon" } };
        } else if (pointOutcome.outcome === Outcome.Paused) {
            details = { iconProps: { iconName: "CirclePauseSolid", className: "active-icon" } };
        } else if (pointOutcome.outcome === Outcome.InProgress) {
            details = { iconProps: { iconName: "AwayStatus", className: "active-icon" } };
        } else {
            details = { iconProps: { iconName: "CircleFill", className: "active-icon" } };
        }
        details.text = pointOutcome.outcomeString;
        return details;
    }
}
